import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserState } from "./userTypes";
import {
  loginSuccess,
  loginFailure,
  loginInitiateSuccess,
  verifyOtpRequest,
  verifyOtpFailure,
  logout,
  loginRequest,
  sendOtpRequest,
  sendOtpSuccess,
  sendOtpFailure,
  registerFailure,
  registerRequest,
  registerSuccess,
} from "./userActions";
import { SessionManager } from "../../../services/auth/sessionManager";
import { REHYDRATE } from "redux-persist";

const initialState: UserState = {
  openLogout: false,
  userSession: {},
  addAddress: false,
  savingNewAddress: false,
  verifyEmailResponse: {},
  subscription: {
    isLoading: false,
    list: [],
    error: null,
  },
  updateEmailResponse: {},
  updatePhoneResponse: {},
  validateOTPResponse: {},
  resendOTPResponse: {},
  loading: false,
  loginError: null,
  isAuthenticated: SessionManager.hasValidToken(),

  // register state
  registerLoading: false,
  registerError: null,
  registerSuccessMessage: null,
  registerSuccess: false,
  
  // Login Flow
  // Login Flow
  otpChallengeId: null,
  otpExpiresInSeconds: null,
  canResendAt: null,
  resendCount: 0,
  isOtpLoading: false,
  isOtpRequired: false,
  loginStep: 'credentials',
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetLoginState(state) {
      state.loginStep = 'credentials';
      state.otpChallengeId = null;
      state.otpExpiresInSeconds = null;
      state.canResendAt = null;
      state.loading = false;
      state.isOtpLoading = false;
      state.loginError = null;
      state.isOtpRequired = false;
      state.resendCount = 0;
    },
    clearRegisterState(state) {
      state.registerLoading = false;
      state.registerError = null;
      state.registerSuccessMessage = "";
      state.registerSuccess = false;
    },

    setLogoutModal(state, action: PayloadAction<boolean>) {
      state.openLogout = action.payload;
    },
    setUserSession(state, action: PayloadAction<Record<string, any>>) {
      state.userSession = action.payload;
    },
    // example reducers to manage array list (add/remove)
    addToSubscriptionList(state, action: PayloadAction<any>) {
      state.subscription.list.push(action.payload);
    },
    removeFromSubscriptionList(state, action: PayloadAction<string>) {
      // assume payload is an id
      state.subscription.list = state.subscription.list.filter(
        (it) => it.id !== action.payload,
      );
    },
    replaceSubscriptionList(state, action: PayloadAction<any[]>) {
      state.subscription.list = action.payload;
    },
    // auth letest 20122025
    setAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
      if (!action.payload) state.userSession = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE as any, (state, action: any) => {
      const incoming = action.payload?.user;
      if (!incoming) return;

      state.isAuthenticated = SessionManager.hasValidToken();
      state.loading = false;

      const inSub = incoming.subscription ?? {};
      state.subscription = {
        list: Array.isArray(inSub.list) ? inSub.list : [],
        isLoading: false,
        error: null,
      };
    });

    builder.addCase(loginRequest, (state) => {
        state.subscription.isLoading = true;
        state.loading = true;
        state.subscription.error = "";
        state.loginError = null;
      })
      .addCase(loginInitiateSuccess, (state, action) => {
        state.loading = false;
        state.otpChallengeId = action.payload.otpChallengeId;
        state.isOtpRequired = action.payload.isOtpRequired;
        state.loginStep = 'otp';
      })
      .addCase(verifyOtpRequest, (state) => {
        state.loading = true;
        state.loginError = null;
      })
      .addCase(verifyOtpFailure, (state, action) => {
        state.loading = false;
        state.loginError = action.payload;
        // Do not reset step, let user try again
      })
      .addCase(loginSuccess, (state, action) => {
        state.userSession = action.payload; // verify otp response has user details
        state.isAuthenticated = true;
        state.subscription.isLoading = true;
        state.loading = false;
      })
      .addCase(loginFailure, (state, action) => {
        state.userSession = {};
        state.isAuthenticated = false;
        state.subscription.error = action.payload;
        state.subscription.isLoading = false;
        state.loading = false;
        state.subscription.isLoading = false;
        state.loading = false;
        state.loginError = action.payload;
      })
      
      .addCase(sendOtpRequest, (state) => {
          // state.isOtpLoading = true;
          state.loading = true; // reusing main loading for now or separate?
          state.loginError = null;
      })
      .addCase(sendOtpSuccess, (state, action) => {
          state.loading = false;
          // state.isOtpLoading = false;
          // userActions: otpChallengeId also comes from here, but same as login result?
          // The API returns OtpChallengeId (capital O maybe? Dto says OtpChallengeId)
          if(action.payload.OtpChallengeId) {
             state.otpChallengeId = action.payload.OtpChallengeId;
          }
           state.canResendAt = action.payload.canResendAt;
           state.resendCount = action.payload.resendCount;
      })
      .addCase(sendOtpFailure, (state, action) => {
          state.loading = false;
          // state.isOtpLoading = false;
          state.loginError = action.payload;
      });

    // logout
    builder.addCase(logout, (state) => {
      state.userSession = {};
      state.isAuthenticated = false;
      state.loginStep = 'credentials';
      state.otpChallengeId = null;
    });

    // register
    builder
      .addCase(registerRequest, (state) => {
        state.registerLoading = true;
        state.registerError = null;
        state.registerSuccessMessage = null;
        state.registerSuccess = false;
      })
      .addCase(registerSuccess, (state, action) => {
        state.registerLoading = false;
        state.registerError = null;
        state.registerSuccess = true;
        state.registerSuccessMessage =
          (action.payload && action.payload.message) ||
          "OTP sent to your email. Please verify.";
      })
      .addCase(registerFailure, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload;
        state.registerSuccessMessage = null;
        state.registerSuccess = false;
      });
  },
});

export const {
  setAuthenticated,
  setLogoutModal,
  setUserSession,
  addToSubscriptionList,
  removeFromSubscriptionList,
  replaceSubscriptionList,
  resetLoginState,
  clearRegisterState,
} = userSlice.actions;
export default userSlice.reducer;

// fetch session
// builder;
// .addCase(fetchSessionRequest, (state) => {
//   state.loading = true;
// })
// .addCase(fetchSessionSuccess, (state, action) => {
//   state.loading = false;
//   state.isAuthenticated = true;
//   state.userSession = action.payload;
// })
// .addCase(fetchSessionFailure, (state, action) => {
//   state.loading = false;
//   state.isAuthenticated = false;
//   state.userSession = {};
//   state.subscription.error = action.payload;
// });
